import React, { createContext, useEffect, useReducer } from 'react';

import { getMMMFeatureAliases } from '../api/appServices';
import { getMMMFeatureClassifications } from '../api/cube';
import { useGlobalState } from '../hooks/global';

/**
 * Feature Aliases Context and Provider
 *
 * This module provides a React Context for sharing feature aliases across the MMM interface.
 * It fetches two types of feature information and makes them available to all child components:
 * 1. Friendly names for features
 * 2. Feature classifications including channel, platform, audience targeting, and ad type
 *
 * The FeatureAliasesProvider fetches and stores the feature aliases when mounted.
 * Child components can access the aliases using useContext(FeatureAliasesContext).
 *
 * Example usage:
 * ```
 * // In a parent component
 * <FeatureAliasesProvider>
 *   <ChildComponents />
 * </FeatureAliasesProvider>
 *
 * // In any child component
 * const { featureAliases, classifications } = useContext(FeatureAliasesContext);
 * // Access friendly names: featureAliases[featureId]
 * // Access classifications: classifications[featureId]
 * ```
 *
 * @returns {Object} Context value containing:
 *  - featureAliases: Object mapping feature_id to display name
 *  - classifications: Object mapping feature_id to classification details:
 *    - channel: The channel classification
 *    - platform: The platform classification
 *    - audienceTargeting: The audience targeting classification
 *    - adType: The ad type classification
 */

export const FeatureAliasesContext = createContext();

export const FeatureAliasesProvider = ({ children }) => {
  const { jwt } = useGlobalState();

  const initialState = {
    featureAliasesMap: {},
    classifications: {},
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'SET_FEATURE_ALIASES_MAP':
        return {
          ...state,
          featureAliasesMap: action.payload,
        };
      case 'SET_CLASSIFICATIONS':
        return {
          ...state,
          classifications: action.payload,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    getMMMFeatureAliases()
      .then(response => {
        if (response?.data) {
          const mapping = response.data.reduce((accu, item) => ({
            ...accu,
            [item.feature_id]: item.friendly_name,
          }), {});
          dispatch({ type: 'SET_FEATURE_ALIASES_MAP', payload: mapping });
        }
      })
      .catch(error => {
        console.error('Error fetching feature aliases:', error);
      });
  }, []);

  useEffect(() => {
    if (!jwt) return;
    getMMMFeatureClassifications(jwt)
      .then(response => {
        if (response.length) {
          const mapping = response.reduce((accu, item) => ({
            ...accu,
            [item['mmm_feature_aliases.friendly_name']]: {
              channel: item['mmm_feature_aliases.channel'],
              platform: item['mmm_feature_aliases.platform'],
              audienceTargeting: item['mmm_feature_aliases.audience_targeting'],
              adType: item['mmm_feature_aliases.ad_type'],
            },
          }), {});
          dispatch({ type: 'SET_CLASSIFICATIONS', payload: mapping });
        }
      })
      .catch(error => {
        console.error('Error fetching feature aliases:', error);
      });
  }, [jwt]);

  return (
    <FeatureAliasesContext.Provider value={state}>
      {children}
    </FeatureAliasesContext.Provider>
  );
};
